import { observer } from 'mobx-react';
import React from 'react';
import { CloudOff, RefreshCw } from 'lucide-react';
import { CommunityMembershipInfoSupportDto } from '@api/ChallengeClient';
import { Button } from '@components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/card';
import { Skeleton } from '@components/ui/skeleton';
import { UserContext } from '../..';
import { useWrapRequest } from '../../../../components/hooks/use-wrap-request';
import { Api } from '../../../../core/api';
import { links } from '../../../../core/router';
import { injectTSDI } from '../../../../core/tsdi';
import { ChallengesTable, UnverifiedBadge } from './challenges-table';
import { EntityList } from './entity-list';

const plural = (count: number, singular: string, pluralForm: string) =>
    `${count} ${count === 1 ? singular : pluralForm}`;

const summaryOf = (info: CommunityMembershipInfoSupportDto) =>
    [
        plural(info.communities.length, 'community', 'communities'),
        plural(info.teams.length, 'team', 'teams'),
        plural(info.challenges.length, 'challenge', 'challenges')
    ].join(' · ');

const ListSkeleton = () => (
    <div className="flex flex-col gap-3 py-2">
        {['w-3/5', 'w-1/2', 'w-2/3', 'w-2/5', 'w-1/2'].map((width, index) => (
            <Skeleton key={index} className={`h-3 ${width}`} />
        ))}
    </div>
);

export const MembershipsCard = observer(() => {
    const user = React.useContext(UserContext);
    const { challengeClient } = injectTSDI(Api);
    const userId = String(user.id);

    const request = useWrapRequest<CommunityMembershipInfoSupportDto, [string]>(
        async (id) => {
            const result =
                await challengeClient.support.userSupportControllerGetCommunityMembershipInfo(id);
            return result.data;
        },
        { deps: [userId] }
    );

    return (
        <Card>
            <CardHeader className="flex flex-row flex-wrap items-baseline justify-between gap-4">
                <CardTitle>Memberships</CardTitle>
                <span className="text-sm text-muted-foreground">
                    {request.match({ fetched: (info) => summaryOf(info) })}
                </span>
            </CardHeader>
            <CardContent className="flex flex-col gap-7">
                {request.match({
                    loading: () => (
                        <>
                            <ListSkeleton />
                            <ListSkeleton />
                            <ListSkeleton />
                        </>
                    ),
                    error: () => (
                        <div className="flex min-h-60 flex-col items-center justify-center gap-3 rounded-md border border-dashed border-border p-6 text-center">
                            <CloudOff className="size-6 text-muted-foreground" />
                            <p>Couldn't load communities, teams and challenges.</p>
                            <p className="text-sm text-muted-foreground">
                                The memberships service isn't responding. Everything else on this page is up to date.
                            </p>
                            <Button
                                variant="outline"
                                onClick={() => void request.request([userId])}
                            >
                                <RefreshCw /> Retry
                            </Button>
                        </div>
                    ),
                    fetched: (info) => (
                        <>
                            <EntityList
                                label="Communities"
                                emptyText="Not in any community"
                                rows={info.communities.map((community) => ({
                                    id: community.id,
                                    name: community.name,
                                    href: links.communityDetails(String(community.id)),
                                    badge: community.verified ? undefined : <UnverifiedBadge />
                                }))}
                            />
                            <EntityList
                                label="Teams"
                                emptyText="Not in any team"
                                rows={info.teams.map((team) => ({
                                    id: team.id,
                                    name: team.name,
                                    href: links.teamDetails(String(team.id))
                                }))}
                            />
                            <ChallengesTable
                                challenges={info.challenges}
                                communities={info.communities}
                            />
                        </>
                    )
                })}
            </CardContent>
        </Card>
    );
});
